/* ==================================================================
   DELICATEZZA — BRAND STRIP
   File:  assets/dz-brand-strip.js
   BUILD: v7 — 9 Sep 2026
   Pairs with: sections/dz-brand-strip.liquid (v7) and
               assets/dz-brand-strip.css

   Loaded with `defer`, so it runs after the document is parsed and
   never blocks rendering. Every root already exists by then.

   ------------------------------------------------------------------
   WHAT CHANGED IN v7
   ------------------------------------------------------------------
   Up to v6 this was an inline <script> inside the section — 696
   lines, 9.9K gzipped, re-sent on every page view and never cached.
   The behaviour below is unchanged; only two things moved.

   1. It took a section id: `initDzLogoStrip('{{ uid }}')`, then
      looked the root up with [data-dz-lg="id"]. Those two Liquid
      interpolations were the only thing tying 696 lines to a
      particular instance. It now takes the ELEMENT, and finds every
      root itself.

   2. Boot moved to the bottom of this file, and it scans. One copy
      of the script serves every instance on the page.

   The `dzReady` flag that made a repeat call a no-op was already
   there for the theme editor. It now does double duty as the guard
   against the boot scan and a section:load event both reaching the
   same root.

   NO LIQUID IN THIS FILE. Everything the script needs arrives as a
   data- attribute on the root, which is also how v6 did it — the
   script needed the same numbers as the CSS, and parsing them back
   out of a computed style means two places to keep in step and a
   silent drift when they disagree.
   ------------------------------------------------------------------

   WHAT THIS SCRIPT IS FOR. Deliberately small and dependency-free.
   Everything visual is CSS. This does five things only:
     1. keeps `inert` in step with the flip, so the hidden face is
        out of the tab order and out of the accessibility tree —
        backface-visibility does neither
     2. keeps aria-expanded honest
     3. pauses the marquee while any card is open
     4. ignores a tap that was really a drag, so swiping the row on a
        phone does not flip whatever the thumb lifted off
     5. drives and steers the marquee, gated on visibility
   ================================================================== */
(function () {
  'use strict';

  /* The Liquid section may output this asset tag more than once.
   Only the first execution owns the global Shopify lifecycle. */
  if (window.__dzBrandStripV7Loaded) {
    return;
  }

  window.__dzBrandStripV7Loaded = true;

  function initDzLogoStrip(ROOT) {
    /* The theme editor re-renders the whole section on every save,
       and the boot scan below may also reach a root the editor has
       already handed us. The flag makes either a no-op instead of
       stacking a second set of listeners. */
    if (!ROOT || ROOT.dataset.dzReady === '1') { return; }
    ROOT.dataset.dzReady = '1';

    /* Keep track of listeners attached outside ROOT.
    Listeners attached directly to ROOT disappear with the DOM node,
    but window/document listeners otherwise keep this instance alive. */
    var globalListeners = [];

    function addGlobalListener(target, type, handler, options) {
      target.addEventListener(type, handler, options);
      globalListeners.push([target, type, handler, options]);
    }

    /* Px of pointer travel after which a click is treated as the end
       of a swipe rather than a choice. 8 is forgiving enough for a
       thumb resting on glass and tight enough that a real tap always
       lands. */
    var DRAG_TOLERANCE = 8;
    var downX = 0, downY = 0, moved = false;
    var supportsInert = ('inert' in HTMLElement.prototype);

    function setCard(card, open) {
      var front = card.querySelector('.dz-lg__face--front');
      var back  = card.querySelector('.dz-lg__face--back');
      card.classList.toggle('is-flipped', open);
      if (front) {
        front.setAttribute('aria-expanded', open ? 'true' : 'false');
        if (supportsInert) { front.inert = open; }
      }
      if (back && supportsInert) { back.inert = !open; }
    }

    function closeAll(except) {
      var open = ROOT.querySelectorAll('.dz-lg__card.is-flipped');
      for (var i = 0; i < open.length; i++) {
        if (open[i] !== except) { setCard(open[i], false); }
      }
    }

    /* One open card pauses the marquee. Reading something that is
       sliding out from under you is the worst version of this
       section. */
    function syncPause() {
      ROOT.classList.toggle('is-paused', !!ROOT.querySelector('.dz-lg__card.is-flipped'));
    }

    /* Initial state. The markup ships aria-expanded="false", but
       `inert` has to be applied here — it cannot be a static
       attribute or the back face would be inert forever on a browser
       that supports it and never inert on one that does not. */
    var cards = ROOT.querySelectorAll('.dz-lg__card');
    for (var c = 0; c < cards.length; c++) {
      if (cards[c].querySelector('.dz-lg__face--back')) { setCard(cards[c], false); }
    }

    /* Press-and-hold pauses the marquee.

       On desktop :hover does this in CSS. On a phone there is no
       hover, so a marquee would mean asking someone to tap a moving
       target — which is why v1 defaulted mobile to a swipe row
       instead. Holding a finger down stops it, so the card can be
       caught and read before it is tapped.

       `is-held` is a SEPARATE class from `is-paused` on purpose: a
       card can be open (is-paused) while a finger is also down
       (is-held), and lifting the finger must not restart a marquee
       that still has a card open.

       pointerup alone is not enough to clear it — a finger that
       leaves the element, or a gesture the browser takes over for
       scrolling, fires pointercancel or nothing at all. All are
       bound, and the state is a boolean rather than a counter so a
       missed event cannot leave it stuck. */
    function setHeld(on) { ROOT.classList.toggle('is-held', on); }

    ROOT.addEventListener('pointermove', function (e) {
      if (Math.abs(e.clientX - downX) > DRAG_TOLERANCE ||
          Math.abs(e.clientY - downY) > DRAG_TOLERANCE) { moved = true; }
    }, true);

    /* ==============================================================
       STEERABLE MARQUEE

       Everything below replaces the CSS animation with a rAF-driven
       transform, but ONLY once it is running — the `dz-lg--js` class
       is what tells CSS to stand down, and it is added at the end of
       setup, not at the top. If this script never runs, or throws,
       the CSS animation is still there and the row still drifts. It
       simply cannot be steered.

       Why not keep the CSS animation and flip `animation-direction`
       on swipe: switching direction mid-animation restarts it from
       its own start offset, so the row visibly jumps. Matching the
       phase back up needs a computed negative animation-delay, which
       is more fragile than just owning the position outright.

       Why not make the viewport a native scroller and animate
       scrollLeft: it gives free momentum, but the seamless wrap has
       to reposition scrollLeft mid-scroll, and on iOS that cancels
       momentum and hitches once per lap. Owning the transform keeps
       the wrap invisible, because a transform has no scroll state to
       interrupt.

       POSITION MODEL: `x` is how far the track has moved, in px,
       always wrapped into [0, halfW). Because the two halves are
       identical and exactly halfW apart, any x and x + halfW are
       visually indistinguishable — which is what makes the wrap
       free, in BOTH directions. That is the part a scroller cannot
       do: it clamps at zero.
       ============================================================== */
    var track    = ROOT.querySelector('.dz-lg__track');
    var half0    = ROOT.querySelector('.dz-lg__half');
    var VIEWPORT = ROOT.querySelector('.dz-lg__viewport');
    var STEER = ROOT.getAttribute('data-steer') === '1';

    /* Seconds for the track to travel one half — the same number the
       CSS animation uses, handed over as an attribute rather than
       parsed back out of a computed style. */
    var MARQ_DUR = parseFloat(ROOT.getAttribute('data-marq-dur')) || 24;
    var BASE_DIR = ROOT.getAttribute('data-marq-dir') === 'right' ? -1 : 1;

    var mqDesktop = window.matchMedia('(min-width: 641px)');
    var mqReduce  = window.matchMedia('(prefers-reduced-motion: reduce)');

    var x = 0, halfW = 0, vel = 0, dir = BASE_DIR;
    var rafId = null, lastTs = 0, driving = false, dragging = false;
    var dragStartX = 0, xAtDragStart = 0, lastMoveX = 0, lastMoveT = 0;
    var dragPointerId = null, captured = false;

    /* Reduced-motion manual scrolling */
    var manualDrag = false;
    var manualStartX = 0;
    var manualStartScroll = 0;
    /* Hover and focus pause state. Declared HERE, with the rest of
       the engine state, because paused() below reads them — `var`
       hoisting made the old placement harmless, but a function
       reading state defined further down the file reads like a bug
       to anyone reviewing it. */
    var hoverPaused = false, focusPaused = false;
    var pointerInside = false, dwellTimer = null;
    /* Declared before sync() can be called. In v6 these sat further
       down and `var` hoisting covered it; with the boot scan calling
       into each instance the ordering is worth being explicit
       about. */
    var onScreen = true, io = null;

    function currentMode() {
      return mqDesktop.matches
        ? ROOT.getAttribute('data-d-mode')
        : ROOT.getAttribute('data-m-mode');
    }
    /* Reduced motion is checked here, not just at startup. Someone
       can turn it on in OS settings with the page open, and the
       matchMedia listener below re-runs this. */
    function wantsDrive() {
      return currentMode() === 'marquee' && !mqReduce.matches && !!track && !!half0;
    }

    function measure() {
      /* offsetWidth of ONE half is exactly the wrap distance: the
         half carries its own gap and one trailing gap, so its box is
         Σitem + N·gap. Read it rather than computing it — the card
         width is a setting and the gap is a setting, and a
         duplicated calculation is a second thing to keep in step. */
      halfW = half0 ? half0.offsetWidth : 0;
    }

    function wrap() {
      if (halfW > 0) { x = ((x % halfW) + halfW) % halfW; }
    }

    function apply() {
      /* Negative: positive x means the content has travelled left,
         which is the direction the CSS keyframes go by default. */
      track.style.transform = 'translate3d(' + (-x) + 'px, 0, 0)';
    }

    /* ---- pause state ---------------------------------------------
       Four reasons to stop, and ALL FOUR have to be read here.

       In v3 only the two class-based ones were. Hover and
       focus-within were CSS rules setting `animation-play-state`,
       and the moment the script sets `animation: none` to take over
       the transform, `animation-play-state` has no animation left to
       pause — so both silently stopped working. The CSS rules are
       still in the stylesheet and still correct for the no-JS
       baseline; they just cannot be the only implementation any
       more.

       General lesson for anything added later: if you pause the
       marquee from CSS, you must pause it here too, or it will work
       without JavaScript and not with it. That is a horrible bug to
       find. */
    function paused() {
      return ROOT.classList.contains('is-paused') ||
             ROOT.classList.contains('is-held') ||
             hoverPaused ||
             focusPaused;
    }

    /* ---- hover: stop on dwell, resume on movement ----------------
       "Pointer is somewhere over the row" is a blunt condition — it
       freezes the whole thing while the cursor is merely travelling
       across on its way somewhere else.

       Dwell mode instead stops it when the pointer SETTLES and
       starts it again the moment it moves, so the row only holds
       still when someone is actually looking at a card. `over` mode
       is the older blunt behaviour, kept as an option.

       Guarded on pointerType === 'mouse': a touch pointer already
       has press-and-hold, and a stationary finger resting on the
       glass should not be read as a dwell. */
    var HOVER_PAUSE = ROOT.getAttribute('data-pause-hover') === '1';
    var HOVER_MODE  = ROOT.getAttribute('data-hover-mode') || 'dwell';
    var DWELL_MS    = parseInt(ROOT.getAttribute('data-dwell'), 10) || 140;

    function armDwell() {
      window.clearTimeout(dwellTimer);
      hoverPaused = false;                    /* moving -> resume  */
      dwellTimer = window.setTimeout(function () {
        if (pointerInside) { hoverPaused = true; }   /* settled -> stop */
      }, DWELL_MS);
    }
    function clearHover() {
      window.clearTimeout(dwellTimer);
      pointerInside = false;
      hoverPaused = false;
    }

    if (VIEWPORT && HOVER_PAUSE) {
      VIEWPORT.addEventListener('pointerenter', function (e) {
        if (e.pointerType !== 'mouse') { return; }
        pointerInside = true;
        if (HOVER_MODE === 'over') { hoverPaused = true; } else { armDwell(); }
      });
      VIEWPORT.addEventListener('pointermove', function (e) {
        if (e.pointerType !== 'mouse' || !pointerInside) { return; }
        if (HOVER_MODE === 'dwell') { armDwell(); }
      });
      VIEWPORT.addEventListener('pointerleave', function (e) {
        if (e.pointerType !== 'mouse') { return; }
        clearHover();
      });
    }

    /* KEYBOARD focus stops it outright — there is no "dwell" for a
       keyboard, and a row moving under someone tabbing through it is
       unusable.

       The word KEYBOARD is doing all the work here. v4 paused on any
       `focusin`, and that produced two bugs that looked unrelated:

         1. Clicking a card focused its button. Focus then stayed
            there indefinitely, so the marquee stopped for good —
            moving the mouse away did nothing, because focus is not
            hover.

         2. Switching to another application fired `focusout` with a
            null relatedTarget, which cleared the flag and set the
            row running again. Clicking back into the browser
            restored focus to that same button and stopped it dead.
            So it ran while you were looking at something else and
            froze whenever you came back — precisely backwards.

       `:focus-visible` is the browser's own answer to "was this
       focus keyboard-driven", including its heuristics for a
       restored window. matches() throws on an unknown pseudo in
       older engines, so it is wrapped — and the fallback is FALSE,
       i.e. keep moving. A marquee that will not stop is a much
       smaller problem than one that will not start. */
    function keyboardFocused(el) {
      if (!el || !el.matches) { return false; }
      try { return el.matches(':focus-visible'); } catch (err) { return false; }
    }

    ROOT.addEventListener('focusin', function (e) {
      focusPaused = keyboardFocused(e.target);
    });
    ROOT.addEventListener('focusout', function (e) {
      if (!ROOT.contains(e.relatedTarget)) { focusPaused = false; }
    });

    /* A window that loses focus can swallow the pointerleave, which
       would strand hoverPaused/is-held as true and stop the row
       permanently. Clear both on blur; whatever is genuinely still
       true will re-assert itself on the next pointer event. */
    addGlobalListener(window, 'blur', function () {
      clearHover();
      ROOT.classList.remove('is-held');
    });

    /* Clicking anywhere that is not a card closes an open one.

       Without this an open card is a dead end: it sets `is-paused`
       by design, and the only ways back were clicking that exact
       card again or pressing Escape. Anyone who opened one, looked
       away and clicked elsewhere was left with a permanently stopped
       marquee and no obvious cause.

       NOTE this is a document-level listener and there is one per
       instance. The first line makes it return immediately unless
       THIS instance has an open card, so two strips on a page do not
       interfere. */
    addGlobalListener(document, 'click', function (e) {
      if (!ROOT.querySelector('.dz-lg__card.is-flipped')) { return; }
      if (e.target instanceof Element && e.target.closest('.dz-lg__card')) { return; }
      closeAll(null);
      syncPause();
    });

    function tick(ts) {
      if (!driving) { return; }
      /* Clamp the delta. A backgrounded tab, a breakpoint on the
         page, or simply a slow first frame can hand back a delta of
         seconds — unclamped, the row would teleport. rAF does not
         fire in a hidden tab at all, so the first frame after
         returning is exactly this case. */
      var dt = lastTs ? Math.min(ts - lastTs, 50) : 16;
      lastTs = ts;

      if (!dragging && !paused() && halfW > 0) {
        var base = dir * (halfW / (MARQ_DUR * 1000));
        /* A fling decays back to the base speed rather than to a
           stop, so the row never halts unexpectedly — it just
           carries on in whichever direction it was thrown. The
           exponential is framed in dt so it behaves identically at
           60Hz and 120Hz. */
        if (Math.abs(vel) > Math.abs(base)) {
          vel += (base - vel) * (1 - Math.pow(0.9965, dt));
        } else {
          vel = base;
        }
        x += vel * dt;
        wrap();
        apply();
      }
      rafId = window.requestAnimationFrame(tick);
    }

    /* CLAIM is separate from RUN, and the separation is the whole
       point of the off-screen gate.

       `dz-lg--js` is what switches the CSS keyframe animation off.
       It has to be added the moment JS takes responsibility — NOT
       when the frame loop starts. Tying it to the loop meant that
       while the section sat off-screen with the loop stopped, the
       class was absent and the CSS animation ran in its place. The
       gate would have saved the rAF work and left the compositor
       animating a row nobody could see: a near-zero net win, and
       invisible unless you go looking. */
    function claim() {
      measure();
      ROOT.classList.add('dz-lg--js');
      apply();
    }

    /* `resume` distinguishes coming back on-screen from a cold
       start. On resume the velocity and direction are left alone, so
       a row the customer had steered carries on the way they threw
       it rather than snapping back to the default. lastTs is always
       reset — the gap since the last frame is meaningless and would
       otherwise be clamped-but-still-wrong. */
    function startDrive(resume) {
      if (driving) { return; }
      driving = true;
      claim();
      lastTs = 0;
      if (!resume) { vel = dir * (halfW / (MARQ_DUR * 1000)); }
      rafId = window.requestAnimationFrame(tick);
    }

    function stopDrive() {
      driving = false;
      if (rafId) { window.cancelAnimationFrame(rafId); rafId = null; }
      ROOT.classList.remove('dz-lg--js', 'is-dragging');
      /* Hand the transform back to CSS. Leaving an inline translate
         behind would offset a grid or a static row. */
      if (track) { track.style.transform = ''; }
    }

    function pauseDrive() {
      if (!driving) { return; }
      driving = false;
      if (rafId) { window.cancelAnimationFrame(rafId); rafId = null; }
      /* Class and transform deliberately left in place. */
    }

    /* stopDrive() is a FULL teardown and belongs only to a real mode
       change or reduced motion — it hands the transform back to CSS.
       Being off-screen is not that: the section still wants to be a
       JS-driven marquee, it just should not be spending frames right
       now. Calling stopDrive() here (an earlier draft did) would
       restart the CSS animation off-screen on the next resize. */
    function sync() {
      if (!wantsDrive()) { stopDrive(); return; }
      claim();
      if (onScreen) { startDrive(true); } else { pauseDrive(); }
    }

    /* ==============================================================
       OFF-SCREEN GATE

       Without this the rAF loop runs for the life of the page —
       writing a transform every frame for a row that may be three
       screens above the fold, or long since scrolled past. It is
       invisible in testing and shows up as main-thread time and
       battery drain in the field.

       Two distinct stops, and the difference matters:

         pauseDrive()  cancels the frame loop but KEEPS the
                       `dz-lg--js` class and the current transform.
                       Position is preserved and, crucially, the CSS
                       animation stays suppressed — dropping the
                       class here would hand the row back to the
                       keyframes and it would animate off-screen
                       anyway, which is the thing being avoided.

         stopDrive()   full teardown, for a real mode change or
                       reduced motion. Clears the class and the
                       inline transform so CSS takes over cleanly.

       rootMargin starts it slightly before the row is visible, so it
       is already moving when it arrives rather than jerking into
       life. Images are promoted at the same moment.
       ============================================================== */

    /* Native lazy loading cannot see these images. They sit inside an
       overflow:hidden container and are moved by a transform, so
       anything beyond the first screenful never intersects the
       viewport until the marquee carries it in — which is exactly
       the pop-in that got reported.

       Promoting `loading` from lazy to eager is defined by the HTML
       spec to start the load, so this is a real fetch trigger rather
       than a hint. Doing it here means the page pays for these
       images only if someone actually scrolls near the section —
       strictly better than the eager-on-render approach it replaces,
       which cost the requests on every page view. */
    function promoteLogos() {
      var imgs = ROOT.querySelectorAll('img.dz-lg__logo[loading="lazy"]');
      for (var i = 0; i < imgs.length; i++) { imgs[i].loading = 'eager'; }
    }

    if (window.IntersectionObserver) {
      /*
      * Work out the initial state immediately.
      *
      * Previously we set onScreen=false and waited for the observer's
      * first callback. Usually that arrives immediately, but the
      * animation should not depend on that callback in order to start.
      */
      var initialRect = ROOT.getBoundingClientRect();

      onScreen =
        initialRect.bottom >= -300 &&
        initialRect.top <= window.innerHeight + 300;

      if (onScreen) {
        promoteLogos();
      }

      io = new window.IntersectionObserver(function (entries) {
        for (var i = 0; i < entries.length; i++) {
          onScreen = entries[i].isIntersecting;

          if (onScreen) {
            promoteLogos();

            if (wantsDrive()) {
              startDrive(true);
            }
          } else {
            pauseDrive();
          }
        }
      }, { rootMargin: '300px 0px' });

      io.observe(ROOT);
    }

    /* ---- pointer: hold to pause, drag to steer -------------------- */
    ROOT.addEventListener('pointerdown', function (e) {
      downX = e.clientX;
      downY = e.clientY;
      moved = false;

      setHeld(true);

      /* Don't drag while interacting with the back of a flipped card. */
      if (
        e.target instanceof Element &&
        e.target.closest('.dz-lg__face--back')
      ) {
        return;
      }

      /*
      * Reduced motion:
      * no autoplay, but allow the viewport to be dragged manually
      * with a mouse just like a touch scroller.
      */
      if (
        mqReduce.matches &&
        currentMode() === 'marquee' &&
        VIEWPORT
      ) {
        manualDrag = true;
        manualStartX = e.clientX;
        manualStartScroll = VIEWPORT.scrollLeft;
        dragPointerId = e.pointerId;
        captured = false;
        return;
      }

      if (!driving || !STEER) { return; }

      dragging = true;
      dragStartX = e.clientX;
      xAtDragStart = x;
      lastMoveX = e.clientX;
      lastMoveT = e.timeStamp || performance.now();
      vel = 0;
      dragPointerId = e.pointerId;
      captured = false;
      /* NOTE: pointer capture is NOT taken here. See the comment on
         captureIfMoved() below — taking it on pointerdown is what
         stopped the cards flipping in v3. */
    }, true);

    /* Capture only once the gesture is genuinely a drag.

       v3 called setPointerCapture on every pointerdown, and that
       broke the flip completely. Once an element has pointer capture
       the browser dispatches the following `click` to the CAPTURE
       TARGET — the section root — rather than to the card under the
       finger. The click handler's
       `e.target.closest('[data-dz-flip]')` then found nothing and no
       card ever turned. Steering still worked, because that path
       only uses pointer events, which is why it looked like the flip
       alone had broken.

       Capturing lazily fixes it at the source: a tap never captures,
       so its click is dispatched normally; a drag captures the
       moment it passes the threshold, which is all the capture was
       ever needed for. */
    function captureIfMoved() {
      if (captured || dragPointerId == null || !ROOT.setPointerCapture) { return; }
      captured = true;
      try { ROOT.setPointerCapture(dragPointerId); } catch (err) {}
    }

    ROOT.addEventListener('pointermove', function (e) {

      /* Reduced-motion manual horizontal drag */
      if (manualDrag && VIEWPORT) {
        var dx = e.clientX - manualStartX;

        if (Math.abs(dx) > DRAG_TOLERANCE) {
          moved = true;
          ROOT.classList.add('is-manual-dragging');
          captureIfMoved();
        }

        VIEWPORT.scrollLeft = manualStartScroll - dx;
        return;
      }

      if (!dragging) { return; }
      if (Math.abs(e.clientX - dragStartX) > DRAG_TOLERANCE) {
        ROOT.classList.add('is-dragging');
        captureIfMoved();
      }
      /* 1:1 with the finger. Anything else feels like lag. */
      x = xAtDragStart - (e.clientX - dragStartX);
      wrap();
      apply();

      var t = e.timeStamp || performance.now();
      var dt = t - lastMoveT;
      if (dt > 0) {
        /* Content velocity is the negative of finger velocity.
           Sampled per move rather than across the whole gesture, so a
           slow drag that ends in a flick throws properly. */
        vel = -(e.clientX - lastMoveX) / dt;
        lastMoveX = e.clientX;
        lastMoveT = t;
      }
    }, true);

    function endDrag(e) {
      setHeld(false);

      if (manualDrag) {
        manualDrag = false;
        ROOT.classList.remove('is-manual-dragging');

        if (
          captured &&
          dragPointerId != null &&
          ROOT.releasePointerCapture
        ) {
          try {
            ROOT.releasePointerCapture(dragPointerId);
          } catch (err) {}
        }

        captured = false;
        dragPointerId = null;
        return;
      }

      if (!dragging) { return; }
      dragging = false;
      ROOT.classList.remove('is-dragging');
      /* Only release what was actually taken — releasing a pointer
         that was never captured throws in some browsers. */
      if (captured && dragPointerId != null && ROOT.releasePointerCapture) {
        try { ROOT.releasePointerCapture(dragPointerId); } catch (err) {}
      }
      captured = false;
      dragPointerId = null;

      /* THE STEER. Any deliberate swipe sets the direction the row
         now travels in; it keeps it until the next swipe. The
         threshold is the same 8px that decides a tap from a drag, so
         a gesture either flips a card or steers the row — never
         both, and never neither. */
      if (moved && Math.abs(vel) > 0.005) { dir = vel > 0 ? 1 : -1; }

      /* Cap the throw. An angry flick can produce several px per
         millisecond, which reads as a blur rather than as speed. */
      var MAXV = 3;
      if (vel >  MAXV) { vel =  MAXV; }
      if (vel < -MAXV) { vel = -MAXV; }
      lastTs = 0;
    }

    ROOT.addEventListener('pointerup',     endDrag, true);
    ROOT.addEventListener('pointercancel', endDrag, true);
    ROOT.addEventListener('pointerleave',  function (e) {
      /* Only clears the hold. With pointer capture a drag should
         survive leaving the element, so it is not ended here. */
      if (!dragging) { setHeld(false); }
    }, true);

    /* ---- trackpad / horizontal wheel ---------------------------- */
    ROOT.addEventListener('wheel', function (e) {
      if (!driving || !STEER) { return; }
      /* Horizontal-dominant only, and preventDefault ONLY then — a
         vertical wheel has to keep scrolling the page. This is why
         the listener cannot be passive. */
      if (Math.abs(e.deltaX) <= Math.abs(e.deltaY)) { return; }
      e.preventDefault();
      x += e.deltaX;
      wrap();
      apply();
      if (Math.abs(e.deltaX) > 1) { dir = e.deltaX > 0 ? 1 : -1; }
      vel = Math.max(-3, Math.min(3, e.deltaX / 16));
    }, { passive: false });

    /* ---- keep in step with the world --------------------------- */
    var reMeasure = null;
    addGlobalListener(window, 'resize', function () {
      window.clearTimeout(reMeasure);

      reMeasure = window.setTimeout(function () {
        measure();
        wrap();

        var rect = ROOT.getBoundingClientRect();

        onScreen =
          rect.bottom >= -300 &&
          rect.top <= window.innerHeight + 300;

        sync();
      }, 150);
    });
    /* Fonts and lazy images change the half's width after first
       paint. Re-measuring on load keeps the wrap distance honest;
       without it the seam drifts by however much the row grew.

       v7 note: this script is deferred, so on a slow connection the
       `load` event may already have fired by the time we get here.
       readyState is checked so the measure still happens. */
    if (document.readyState === 'complete') {
      measure(); wrap();
    } else {
      addGlobalListener(window, 'load', function () { measure(); wrap(); });
    }
    addGlobalListener(document, 'visibilitychange', function () {
      lastTs = 0;

      if (document.hidden) {
        pauseDrive();
        return;
      }

      /*
      * Re-evaluate visibility ourselves when returning to the tab.
      * Do not rely on IntersectionObserver delivering another callback.
      */
      if (ROOT && ROOT.getBoundingClientRect) {
        var rect = ROOT.getBoundingClientRect();
        onScreen =
          rect.bottom >= -300 &&
          rect.top <= window.innerHeight + 300;
      }

      if (wantsDrive()) {
        claim();

        if (onScreen) {
          startDrive(true);
        } else {
          pauseDrive();
        }
      }
    });

    if (mqDesktop.addEventListener) {
      addGlobalListener(mqDesktop, 'change', sync);
    }

    if (mqReduce.addEventListener) {
      addGlobalListener(mqReduce, 'change', sync);
    }

    sync();

    ROOT.addEventListener('click', function (e) {
      /* e.target can be an SVG node or a text node. Element#closest
         exists on SVGElement, but guard anyway — a thrown error here
         would kill every card on the page. */
      if (!(e.target instanceof Element)) { return; }

      /* The back face's button is a real link. Let it navigate. */
      if (e.target.closest('.dz-lg__backbtn')) { return; }

      var closer = e.target.closest('[data-dz-close]');
      if (closer) {
        var openCard = closer.closest('.dz-lg__card');
        if (openCard) {
          var wasKeyboard = keyboardFocused(closer) ||
                            keyboardFocused(document.activeElement);
          setCard(openCard, false);
          syncPause();
          /* Focus goes back where it came from — but only for a
             keyboard user. Moving focus after a MOUSE click is what
             parked it on a card button and stopped the marquee for
             good. setCard has already cleared inert on the front, so
             this focus call lands when it is wanted. */
          var backTo = openCard.querySelector('.dz-lg__face--front');
          if (wasKeyboard && backTo && backTo.focus) {
            backTo.focus();
          } else if (openCard.contains(document.activeElement) &&
                     document.activeElement.blur) {
            /* Otherwise drop focus rather than leaving it on an
               element that is about to become inert. */
            document.activeElement.blur();
          }
        }
        return;
      }

      var trigger = e.target.closest('[data-dz-flip]');
      /* Belt and braces for the v3 flip bug. Pointer capture
         retargets `click` to the capturing element; capture is now
         taken lazily so this should never be needed, but if any
         browser still hands us the root, find the card under the
         cursor rather than silently doing nothing. */
      if (!trigger && e.target === ROOT && document.elementFromPoint) {
        var under = document.elementFromPoint(e.clientX, e.clientY);
        if (under && under.closest) { trigger = under.closest('[data-dz-flip]'); }
      }
      if (!trigger) { return; }

      /* A tap that followed a drag was a scroll. Consume it. */
      if (moved) { moved = false; return; }

      var card = trigger.closest('.dz-lg__card');
      if (!card) { return; }

      /* One at a time. Several open cards on a moving row is noise,
         and it makes Escape ambiguous. */
      var openedByKeyboard = keyboardFocused(trigger);

      closeAll(card);
      setCard(card, true);
      syncPause();

      /* Only a keyboard user gets focus moved onto the back face.
         For a mouse click, moving focus there both draws a focus
         ring nobody asked for and — before this was fixed — left
         focus parked inside the section, stopping the marquee until
         the page was reloaded. */
      var closeBtn = card.querySelector('.dz-lg__closebtn');
      if (openedByKeyboard && closeBtn && closeBtn.focus) {
        closeBtn.focus();
      } else if (trigger.blur) {
        trigger.blur();
      }
    });

    ROOT.addEventListener('keydown', function (e) {
      if (e.key !== 'Escape' && e.key !== 'Esc') { return; }
      if (!(e.target instanceof Element)) { return; }
      var openCard = e.target.closest('.dz-lg__card.is-flipped');
      closeAll(null);
      syncPause();
      if (openCard) {
        var backTo = openCard.querySelector('.dz-lg__face--front');
        if (backTo && backTo.focus) { backTo.focus(); }
      }
    });

    /* Shopify Theme Editor can remove and recreate this section without
      reloading the page. Release everything that lives outside ROOT. */
    ROOT.__dzCleanup = function () {
      driving = false;

      if (rafId) {
        window.cancelAnimationFrame(rafId);
        rafId = null;
      }

      if (io) {
        io.disconnect();
        io = null;
      }

      if (reMeasure) {
        window.clearTimeout(reMeasure);
        reMeasure = null;
      }

      for (var i = 0; i < globalListeners.length; i++) {
        var item = globalListeners[i];

        item[0].removeEventListener(
          item[1],
          item[2],
          item[3]
        );
      }

      globalListeners.length = 0;

      delete ROOT.__dzCleanup;
      delete ROOT.dataset.dzReady;
    };
  }

  /* ==================================================================
     BOOT

     v6 was inline in the section and knew its own id. A shared asset
     does not, so it finds every root instead. `data-dz-lg` was
     already on the root in v6 — it carried the section id for the
     lookup — so no markup change was needed for this.

     initDzLogoStrip is idempotent per element (the dzReady flag), so
     it is safe for the scan and a section:load event to both reach
     the same root.
     ================================================================== */
  function bootAll(scope) {
    var root = scope || document;
    /* The scope of a shopify:section:load event may itself be the
       strip root rather than an ancestor of it. */
    if (root.matches && root.matches('[data-dz-lg]')) {
      initDzLogoStrip(root);
      return;
    }
    var roots = root.querySelectorAll('[data-dz-lg]');
    for (var i = 0; i < roots.length; i++) { initDzLogoStrip(roots[i]); }
  }

  /* This file is loaded with `defer`, so the document is parsed by
     the time it runs and every root already exists. The readyState
     check is for the case where somebody drops the defer attribute —
     it costs one comparison and turns a silent no-op into a working
     section. */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { bootAll(document); });
  } else {
    bootAll(document);
  }

  /* The theme editor fires this after re-rendering a section. The
     dzReady guard makes a duplicate call harmless. */
    document.addEventListener('shopify:section:load', function (e) {
      bootAll(e.target || document);
    });
    document.addEventListener('shopify:section:unload', function (e) {
    var scope = e.target || document;

    if (
      scope.matches &&
      scope.matches('[data-dz-lg]') &&
      scope.__dzCleanup
    ) {
      scope.__dzCleanup();
    }

    if (!scope.querySelectorAll) {
      return;
    }

    var roots = scope.querySelectorAll('[data-dz-lg]');

    for (var i = 0; i < roots.length; i++) {
      if (roots[i].__dzCleanup) {
        roots[i].__dzCleanup();
      }
    }
  });
})();
